import React, { useMemo, useState } from 'react';
import Avatar from '../schedule/Avatar';
import {
    archiveEmployeeInList,
    collectEmployeeEventHistory,
    composeEmployeeName,
    createTemporaryEmployee,
    employeeNameExists,
    getEmployeeName,
    listTemporaryEmployees,
    promoteEmployeeInList,
    renameEmployeeInEvents,
    splitEmployeeName,
    unarchiveEmployee,
    upsertTemporaryInList,
} from '../utils/employees';

const emptyForm = {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    note: '',
};

export default function TemporaryEmployeesPanel({
    employees = [],
    eventsByDate = {},
    updateEmployees,
    updateEmployeesAndEvents,
}) {
    const [form, setForm] = useState(emptyForm);
    const [editName, setEditName] = useState(null);
    const [showArchived, setShowArchived] = useState(false);
    const [expandedName, setExpandedName] = useState(null);
    const [message, setMessage] = useState('');

    const temps = useMemo(
        () => listTemporaryEmployees(employees, { includeArchived: showArchived }),
        [employees, showArchived]
    );

    const persist = (nextEmployees, nextEvents) => {
        if (nextEvents && typeof updateEmployeesAndEvents === 'function') {
            updateEmployeesAndEvents(nextEmployees, nextEvents);
            return;
        }
        updateEmployees(nextEmployees);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const name = composeEmployeeName(form.firstName, form.lastName);
        if (!name) {
            setMessage('Podaj imię i nazwisko.');
            return;
        }
        if (employeeNameExists(employees, name, { exceptName: editName })) {
            setMessage(`Osoba „${name}” już istnieje.`);
            return;
        }

        if (editName) {
            const created = createTemporaryEmployee(form);
            const nextEmployees = employees.map((emp) => (
                getEmployeeName(emp) === editName
                    ? { ...emp, ...created, isTemporary: true, archived: emp.archived === true }
                    : emp
            ));
            const nextEvents = editName !== name
                ? renameEmployeeInEvents(eventsByDate, editName, name)
                : undefined;
            persist(nextEmployees, nextEvents);
            setEditName(null);
            setForm(emptyForm);
            setMessage('Zapisano dane osoby tymczasowej.');
            return;
        }

        const result = upsertTemporaryInList(employees, form);
        if (result.error) {
            setMessage(result.error);
            return;
        }
        updateEmployees(result.employees);
        setForm(emptyForm);
        setMessage('Dodano osobę tymczasową.');
    };

    const startEdit = (emp) => {
        const name = getEmployeeName(emp);
        const split = {
            firstName: emp.firstName || splitEmployeeName(name).firstName,
            lastName: emp.lastName || splitEmployeeName(name).lastName,
        };
        setEditName(name);
        setForm({
            firstName: split.firstName,
            lastName: split.lastName,
            phone: emp.phone || '',
            email: emp.email || '',
            note: emp.note || '',
        });
        setMessage('');
    };

    return (
        <div className="space-y-6">
            <div className="bg-gray-800 border border-gray-700 rounded p-4">
                <h3 className="text-lg font-semibold mb-2">
                    {editName ? `Edytuj: ${editName}` : 'Dodaj osobę tymczasową'}
                </h3>
                <p className="text-sm text-gray-400 mb-4">
                    Osoby tymczasowe nie pojawiają się na stałej liście pracowników, w dostępnościach
                    ani w statystykach. Możesz je przypisać tylko w obsadzie wydarzenia.
                </p>
                <form onSubmit={handleSubmit} className="grid gap-2 md:grid-cols-2">
                    <label className="text-sm">
                        Imię
                        <input
                            value={form.firstName}
                            onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value }))}
                            className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-3 py-2"
                            autoComplete="given-name"
                        />
                    </label>
                    <label className="text-sm">
                        Nazwisko
                        <input
                            value={form.lastName}
                            onChange={(e) => setForm((prev) => ({ ...prev, lastName: e.target.value }))}
                            className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-3 py-2"
                            autoComplete="family-name"
                        />
                    </label>
                    <label className="text-sm">
                        Telefon
                        <input
                            value={form.phone}
                            onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                            className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-3 py-2"
                            autoComplete="tel"
                        />
                    </label>
                    <label className="text-sm">
                        Email
                        <input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                            className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-3 py-2"
                            autoComplete="email"
                        />
                    </label>
                    <label className="text-sm md:col-span-2">
                        Notatka (np. specjalizacja)
                        <input
                            value={form.note}
                            onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
                            className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-3 py-2"
                        />
                    </label>
                    <div className="md:col-span-2 flex flex-wrap gap-2">
                        <button type="submit" className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-500">
                            {editName ? 'Zapisz' : 'Dodaj'}
                        </button>
                        {editName && (
                            <button
                                type="button"
                                className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600"
                                onClick={() => {
                                    setEditName(null);
                                    setForm(emptyForm);
                                }}
                            >
                                Anuluj
                            </button>
                        )}
                    </div>
                </form>
                {message && <p className="mt-3 text-sm text-gray-300">{message}</p>}
            </div>

            <div>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold">Lista osób tymczasowych</h3>
                    <label className="text-sm flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={showArchived}
                            onChange={(e) => setShowArchived(e.target.checked)}
                        />
                        Pokaż zarchiwizowane
                    </label>
                </div>

                {temps.length === 0 ? (
                    <p className="text-sm text-gray-400">Brak osób tymczasowych.</p>
                ) : (
                    <div className="space-y-2">
                        {temps.map((emp) => {
                            const name = getEmployeeName(emp);
                            const history = collectEmployeeEventHistory(eventsByDate, name);
                            const expanded = expandedName === name;
                            return (
                                <div key={name} className="bg-gray-800 px-3 py-3 rounded border border-gray-700">
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <Avatar employee={emp} size={36} title={name} />
                                            <div className="min-w-0">
                                                <div className="font-medium truncate">
                                                    {name}
                                                    {emp.archived && (
                                                        <span className="ml-2 text-xs text-gray-400">zarchiwizowany</span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-gray-400 truncate">
                                                    {[emp.phone, emp.email, emp.note].filter(Boolean).join(' · ') || 'Brak danych kontaktowych'}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            <button
                                                type="button"
                                                className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-sm"
                                                onClick={() => setExpandedName(expanded ? null : name)}
                                            >
                                                Historia ({history.length})
                                            </button>
                                            <button
                                                type="button"
                                                className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-sm"
                                                onClick={() => startEdit(emp)}
                                            >
                                                Edytuj
                                            </button>
                                            {emp.archived ? (
                                                <button
                                                    type="button"
                                                    className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-sm"
                                                    onClick={() => {
                                                        updateEmployees(employees.map((item) => (
                                                            getEmployeeName(item) === name ? unarchiveEmployee(item) : item
                                                        )));
                                                    }}
                                                >
                                                    Przywróć
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-sm"
                                                    onClick={() => updateEmployees(archiveEmployeeInList(employees, name))}
                                                >
                                                    Archiwizuj
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="px-2 py-1 rounded bg-blue-700 hover:bg-blue-600 text-sm"
                                                onClick={() => {
                                                    updateEmployees(promoteEmployeeInList(employees, name));
                                                    setMessage(`„${name}” jest teraz stałym pracownikiem. Historia przypisań została zachowana.`);
                                                }}
                                            >
                                                Awansuj
                                            </button>
                                        </div>
                                    </div>
                                    {expanded && (
                                        <div className="mt-3 border-t border-gray-700 pt-3 text-sm">
                                            {history.length === 0 ? (
                                                <p className="text-gray-400">Brak przypisań do wydarzeń.</p>
                                            ) : (
                                                <ul className="space-y-1">
                                                    {history.map((item) => (
                                                        <li key={`${item.date}-${item.eventName}-${item.calendarId}`}>
                                                            {item.date} — {item.eventName}
                                                            {item.tentative ? ' (?)' : ''}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
